// Cognito sign-in and TOTP two-factor helpers shared by every oller.io site.
//
// createCognitoAuth() wraps one user pool; every site builds its own instance
// from its config and passes it to the components in this package.
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

// Shown in authenticator apps next to the six-digit code.
const TOTP_DEVICE_NAME = 'Authenticator app';

// Runs one step of a Cognito sign-in and resolves to { step, user }, where
// step names the screen that comes next: 'done', 'mfa' (enter a code),
// 'mfaSetup' (pair an authenticator) or 'newPassword'.
function runStep(user, invoke) {
  return new Promise((resolve, reject) => {
    const to = (step) => () => resolve({ step, user });
    invoke({
      onSuccess: to('done'),
      onFailure: reject,
      totpRequired: to('mfa'),
      mfaSetup: to('mfaSetup'),
      newPasswordRequired: to('newPassword'),
    });
  });
}

export function createCognitoAuth({ userPoolId, clientId }) {
  const pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });

  const getCurrentUser = () => pool.getCurrentUser();

  const getSession = () => new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) { reject(new Error('Not signed in')); return; }
    user.getSession((err, session) => (err ? reject(err) : resolve(session)));
  });

  const getIdToken = async () => (await getSession()).getIdToken().getJwtToken();

  const getEmail = async () => (await getSession()).getIdToken().decodePayload().email;

  const signIn = (email, password) => {
    const username = email.trim();
    const user = new CognitoUser({ Username: username, Pool: pool });
    const details = new AuthenticationDetails({ Username: username, Password: password });
    return runStep(user, (cb) => user.authenticateUser(details, cb));
  };

  const completeNewPassword = (user, newPassword) =>
    runStep(user, (cb) => user.completeNewPasswordChallenge(newPassword, {}, cb));

  const confirmMfa = (user, code) =>
    runStep(user, (cb) => user.sendMFACode(code, cb, 'SOFTWARE_TOKEN_MFA'));

  // Starts pairing an authenticator app: returns the new base32 secret. Works
  // both mid sign-in (MFA_SETUP challenge) and for a signed-in user (change
  // authenticator). Until finishTotpEnrollment succeeds, the current
  // authenticator keeps working.
  const startTotpEnrollment = (user) =>
    new Promise((resolve, reject) => {
      user.associateSoftwareToken({ associateSecretCode: resolve, onFailure: reject });
    });

  // Confirms the new authenticator with a code from it. From this moment the
  // old authenticator's codes are rejected.
  const finishTotpEnrollment = async (user, code) => {
    await new Promise((resolve, reject) => {
      user.verifySoftwareToken(code, TOTP_DEVICE_NAME, { onSuccess: resolve, onFailure: reject });
    });
    const session = user.getSignInUserSession();
    if (session && session.isValid()) {
      // Signed-in change: make sure TOTP stays the enabled, preferred factor.
      await new Promise((resolve, reject) => {
        user.setUserMfaPreference(null, { Enabled: true, PreferredMfa: true },
          (err, res) => (err ? reject(err) : resolve(res)));
      });
    }
    return { step: 'done', user };
  };

  // "Confirm it's you" before a security change: a full sign-in with the
  // password and a current code. Resolves to a freshly signed-in user.
  const reauthenticate = async (password, code) => {
    const email = await getEmail();
    const first = await signIn(email, password);
    if (first.step === 'done') return first.user;
    if (first.step !== 'mfa') throw new Error('Your account needs attention — sign out and sign in again.');
    await confirmMfa(first.user, code);
    return first.user;
  };

  const signOut = () => {
    const user = getCurrentUser();
    if (user) user.signOut();
  };

  return {
    getCurrentUser,
    getSession,
    getIdToken,
    getEmail,
    signIn,
    completeNewPassword,
    confirmMfa,
    startTotpEnrollment,
    finishTotpEnrollment,
    reauthenticate,
    signOut,
  };
}

// Mid sign-in challenges expire after a few minutes; the flow must restart.
export function isExpiredSession(err) {
  return !!err && (err.code || err.name) === 'NotAuthorizedException' && /session is expired/i.test(err.message || '');
}

// Cognito errors in words a person can act on.
// Pass { passwordOnly: true } where the email isn't being typed (re-auth).
export function friendlyAuthError(err, { passwordOnly = false } = {}) {
  const name = (err && (err.code || err.name)) || '';
  const message = (err && err.message) || '';
  const badLogin = passwordOnly ? 'Incorrect password.' : 'Incorrect email or password.';
  switch (name) {
    case 'NotAuthorizedException':
      if (isExpiredSession(err)) return 'This took too long and timed out. Start again.';
      return badLogin;
    case 'UserNotFoundException':
      return badLogin;
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return 'That code didn’t match. Codes change every 30 seconds — enter the one showing now.';
    case 'ExpiredCodeException':
      return 'That code was already used. Wait for the next one.';
    case 'InvalidPasswordException':
      return message.replace(/^Password does not conform to policy: /, '') || 'That password doesn’t meet the requirements.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'PasswordResetRequiredException':
      return 'Your password needs to be reset. Contact the site owner.';
    default:
      return message || 'Something went wrong. Try again.';
  }
}
